import { GoogleGenAI } from "@google/genai";
import { AuditResult } from "../types";

// ============================================================================
// ORBIT AI CORE CONFIGURATION (GEMINI 3.0 UPGRADE)
// ============================================================================
// STRATEGIE:
// 1. CHATBOTS -> Gemini 3.0 Flash (Next-Gen Speed & Low Latency)
// 2. DEEP AUDIT -> Gemini 3.0 Pro (High-Reasoning mit Thinking Blocks)
// ============================================================================

const getApiKey = () => {
    // 1. Check Browser Storage (Dein privater Paid Key für Tests)
    if (typeof window !== "undefined") {
        const localKey = localStorage.getItem("orbit_custom_api_key");
        if (localKey && localKey.startsWith("AIza")) {
            console.log("💎 ORBIT CORE: Using Paid Admin Key (Gemini 3.0 Ready)");
            return localKey;
        }
    }

    // 2. Check Server Env
    if (process.env.API_KEY && !process.env.API_KEY.includes("EINFUEGEN")) {
        return process.env.API_KEY;
    }

    // 3. Kein Schlüssel: Aufrufer melden das sauber (generateDeepAudit wirft,
    // createChatStream gibt einen Hinweis aus). Ein eingebauter Fallback-Key
    // wäre im Bundle öffentlich lesbar — genau das hatte hier bis 01.08.2026
    // einen echten Google-Key im ausgelieferten JavaScript stehen.
    console.warn("ORBIT SYSTEM: Kein Gemini-Key konfiguriert (localStorage orbit_custom_api_key oder API_KEY).");
    return "";
};

// Client Init
let aiClient: GoogleGenAI | null = null;
const getClient = () => {
    const key = getApiKey();
    if (!key) return null;
    if (!aiClient) {
        aiClient = new GoogleGenAI({ apiKey: key });
    }
    return aiClient;
};

// --- SYSTEM INSTRUCTIONS ---
const SYSTEM_INSTRUCTION_AUDIT = `You are a high-end business automation auditor for 'Orbit Software'. 
Analyze companies based on their industry and name. 
You are running on the heavy 'Gemini 3.0 Pro' model for maximum reasoning capabilities.
Output purely JSON data for the dashboard. No markdown.`;

const SYSTEM_INSTRUCTION_CHAT = `Du bist der 'Orbit Core AI' Assistent (Version 3.0). 
Deine Aufgabe: Beantworte Fragen zu Automatisierung, Kanzlei-Software und KI-Integrationen.
Tonalität: Professionell, präzise, "High-Ticket" Business Deutsch.
Du sprichst mit Anwälten, Steuerberatern oder Geschäftsführern.
Halte Antworten kurz und knackig (unter 100 Wörtern).`;

export const generateDeepAudit = async (companyName: string, industry: string): Promise<AuditResult> => {
  const ai = getClient();
  
  if (!ai) throw new Error("AI Client not initialized");

  try {
    // STRATEGIE: Upgrade auf Gemini 3.0 Pro Preview
    // Wir nutzen 'thinkingConfig', damit das Modell komplexe Zusammenhänge besser versteht.
    const modelId = 'gemini-3-pro-preview'; 
    
    const prompt = `
      Analyze the potential for AI automation for a company named "${companyName}" in the "${industry}" sector.
      Return a JSON object with this exact schema:
      {
        "score": number (0-100),
        "summary": "Executive summary string (sophisticated business language)",
        "opportunities": ["string", "string", "string"],
        "risks": ["string", "string"],
        "estimatedSavings": "string (e.g. €45.000)"
      }
    `;

    const response = await ai.models.generateContent({
      model: modelId,
      contents: prompt,
      config: {
        systemInstruction: SYSTEM_INSTRUCTION_AUDIT,
        responseMimeType: "application/json",
        // Thinking Budget für tiefere Analyse (nur bei Pro Modellen verfügbar)
        thinkingConfig: { thinkingBudget: 1024 } 
      }
    });

    const text = response.text;
    if (!text) throw new Error("No response from AI");
    return JSON.parse(text) as AuditResult;

  } catch (error) {
    console.error("Gemini Audit Error:", error);
    // Fallback Mock Data falls Quota Exceeded
    return {
      score: 88,
      summary: `[FALLBACK] Analyse für ${companyName}. (AI Limit erreicht oder Netzwerkfehler)`,
      opportunities: ["KI-Telefonassistenz", "Automatische Aktenanlage"],
      risks: ["Legacy-Software Schnittstellen"],
      estimatedSavings: "€ 42.000"
    };
  }
};

export async function* createChatStream(history: {role: string, text: string}[], newMessage: string) {
    const ai = getClient();

    if (!ai) {
        yield "System: API Key Konfiguration fehlt.";
        return;
    }

    try {
        const historyText = history.map(h => `${h.role}: ${h.text}`).join('\n');
        const fullPrompt = `${historyText}\nuser: ${newMessage}`;

        // STRATEGIE: Upgrade auf Gemini 3.0 Flash Preview
        // Grund: Beste Latenz am Markt. Fühlt sich an wie Echtzeit.
        const responseStream = await ai.models.generateContentStream({
            model: 'gemini-3-flash-preview',
            contents: fullPrompt,
            config: {
                systemInstruction: SYSTEM_INSTRUCTION_CHAT
            }
        });

        for await (const chunk of responseStream) {
            if (chunk.text) {
                yield chunk.text;
            }
        }

    } catch (e: any) {
        console.error("Chat Error", e);
        if (e.message && e.message.includes('429')) {
             yield "⚠️ Das öffentliche Demo-Limit ist erreicht. Wenn Sie der Administrator sind: Bitte Paid-Key hinterlegen.";
        } else {
             yield "Fehler: Verbindung unterbrochen. Bitte Seite neu laden.";
        }
    }
}